import { useState } from 'react'
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Copy,
  Trash2,
  Play,
  Target,
  Clock,
  Users
} from 'lucide-react'
import { useLibraryStore, CustomExercise } from '@/stores/libraryStore'
import { MuscleGroup, Equipment } from '@/types'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { CreateExerciseModal } from './CreateExerciseModal'
import { EditExerciseModal } from './EditExerciseModal'
import toast from 'react-hot-toast'

export function ExerciseLibraryView() {
  const {
    filteredExercises,
    exercisesLoading,
    exerciseFilters,
    setExerciseFilters,
    setSelectedExercise,
    setIsCreateExerciseModalOpen,
    setIsEditModalOpen,
    deleteExercise,
    duplicateExercise
  } = useLibraryStore()

  const [showFilters, setShowFilters] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const handleSearch = (query: string) => {
    setExerciseFilters({ search: query })
  }

  const handleExerciseAction = (exercise: CustomExercise, action: string) => {
    switch (action) {
      case 'edit':
        setSelectedExercise(exercise)
        setIsEditModalOpen(true)
        break
      case 'duplicate':
        duplicateExercise(exercise.id!)
        toast.success('Exercise duplicated successfully')
        break
      case 'delete':
        if (window.confirm('Are you sure you want to delete this exercise?')) {
          deleteExercise(exercise.id!)
          toast.success('Exercise deleted successfully')
        }
        break
    }
  }

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-700'
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-700'
      case 'advanced':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const muscleGroupOptions: MuscleGroup[] = [
    'chest', 'back', 'shoulders', 'biceps', 'triceps', 'abs', 
    'quads', 'hamstrings', 'glutes', 'calves'
  ]

  const equipmentOptions: Equipment[] = [
    'bodyweight', 'dumbbells', 'barbell', 'kettlebell', 'resistance-bands',
    'pull-up-bar', 'bench', 'cable-machine'
  ]

  if (exercisesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search exercises..."
              value={exerciseFilters.search || ''}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex border border-gray-300 rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm ${
                viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm border-l border-gray-300 ${
                viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              List
            </button>
          </div>

          <button
            onClick={() => setIsCreateExerciseModalOpen(true)}
            className="coach-button-primary px-4 py-2 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Exercise
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Muscle Group
              </label>
              <select
                value={exerciseFilters.muscleGroup || ''}
                onChange={(e) => setExerciseFilters({ muscleGroup: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Muscle Groups</option>
                {muscleGroupOptions.map(muscle => (
                  <option key={muscle} value={muscle}>
                    {muscle.charAt(0).toUpperCase() + muscle.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Equipment
              </label>
              <select
                value={exerciseFilters.equipment || ''}
                onChange={(e) => setExerciseFilters({ equipment: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Equipment</option>
                {equipmentOptions.map(equipment => (
                  <option key={equipment} value={equipment}>
                    {equipment.replace('-', ' ').charAt(0).toUpperCase() + equipment.replace('-', ' ').slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Difficulty
              </label>
              <select
                value={exerciseFilters.difficulty || ''}
                onChange={(e) => setExerciseFilters({ difficulty: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <select
                value={exerciseFilters.isCustom !== undefined ? exerciseFilters.isCustom.toString() : ''}
                onChange={(e) => setExerciseFilters({ 
                  isCustom: e.target.value === '' ? undefined : e.target.value === 'true' 
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Sources</option>
                <option value="true">Custom</option>
                <option value="false">Built-in</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Exercise Grid/List */}
      {filteredExercises.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Target className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No exercises found</h3>
          <p className="text-gray-600 mb-4">
            {exerciseFilters.search || Object.keys(exerciseFilters).length > 0
              ? 'Try adjusting your search or filters'
              : 'Create your first custom exercise to get started'
            }
          </p>
          <button
            onClick={() => setIsCreateExerciseModalOpen(true)}
            className="coach-button-primary px-4 py-2"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Exercise
          </button>
        </div>
      ) : (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
          : 'space-y-4'
        }>
          {filteredExercises.map((exercise) => (
            <div
              key={exercise.id}
              className={`coach-section hover:shadow-md transition-shadow ${
                viewMode === 'list' ? 'flex items-center gap-4' : ''
              }`}
            >
              {/* Exercise Image/Icon */}
              <div className={`${viewMode === 'list' ? 'flex-shrink-0' : 'mb-4'}`}>
                {exercise.imageUrl ? (
                  <img
                    src={exercise.imageUrl}
                    alt={exercise.name}
                    className={`${
                      viewMode === 'list' ? 'w-16 h-16' : 'w-full h-40'
                    } object-cover rounded-lg`}
                  />
                ) : (
                  <div className={`${
                    viewMode === 'list' ? 'w-16 h-16' : 'w-full h-40'
                  } bg-gray-100 rounded-lg flex items-center justify-center`}>
                    <Target className={`${
                      viewMode === 'list' ? 'w-8 h-8' : 'w-12 h-12'
                    } text-gray-400`} />
                  </div>
                )}
              </div>

              {/* Exercise Info */}
              <div className={`${viewMode === 'list' ? 'flex-1' : ''}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{exercise.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      {exercise.difficulty && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(exercise.difficulty)}`}>
                          {exercise.difficulty}
                        </span>
                      )}
                      {exercise.isCustom && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                          Custom
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="relative group">
                    <button className="p-1 text-gray-400 hover:text-gray-600">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                    <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                      <div className="py-1">
                        <button
                          onClick={() => handleExerciseAction(exercise, 'edit')}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleExerciseAction(exercise, 'duplicate')}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </button>
                        {exercise.videoUrl && (
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                            <Play className="w-4 h-4 mr-2" />
                            Watch Video
                          </button>
                        )}
                        <button
                          onClick={() => handleExerciseAction(exercise, 'delete')}
                          className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Exercise Details */}
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {exercise.muscleGroups?.slice(0, 2).join(', ')}
                      {exercise.muscleGroups && exercise.muscleGroups.length > 2 && 
                        ` +${exercise.muscleGroups.length - 2}`
                      }
                    </div>
                    {exercise.targetSets && exercise.targetReps && (
                      <div className="flex items-center">
                        <Target className="w-4 h-4 mr-1" />
                        {exercise.targetSets} × {exercise.targetReps}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center">
                      <span className="text-xs">Equipment:</span>
                      <span className="ml-1">{exercise.equipment?.join(', ') || 'None'}</span>
                    </div>
                    {exercise.restTime && (
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {exercise.restTime}s rest
                      </div>
                    )}
                  </div>

                  {exercise.tags && exercise.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {exercise.tags.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <CreateExerciseModal />
      <EditExerciseModal />
    </div>
  )
}